import React from "react";
import { SkeletonBlock } from "../dashboard/DashboardSkeletons";

const CardHeading = ({ width = 180, withAction = true }: { width?: number; withAction?: boolean }) => (
  <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
    <SkeletonBlock width={width} height={18} />
    {withAction && <SkeletonBlock width={84} height={32} />}
  </div>
);

const HorizontalBars = ({ widths }: { widths: number[] }) => (
  <div className="d-flex flex-column gap-3 py-3">
    {widths.map((width, index) => (
      <div key={index} className="d-flex align-items-center gap-3">
        <SkeletonBlock width={96} height={11} />
        <SkeletonBlock width={`${width}%`} height={22} />
      </div>
    ))}
  </div>
);

const SegmentGridSkeleton = ({ cells = 4 }: { cells?: number }) => (
  <div className="border rounded overflow-hidden">
    <div className="row g-0">
      {Array.from({ length: cells }, (_, index) => (
        <div className="col-sm-6" key={index}>
          <div className={`p-3 nd-skeleton-panel${index % 2 === 0 ? " border-end" : ""}${index < cells - 2 ? " border-bottom" : ""}`}>
            <SkeletonBlock width="55%" height={11} className="mb-2" />
            <div className="d-flex align-items-center justify-content-between">
              <SkeletonBlock width={56} height={18} />
              <SkeletonBlock width={44} height={12} />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const RevenueSummarySkeleton = () => (
  <div aria-hidden="true">
    <div className="row">
      <div className="col-xxl-7 d-flex">
        <div className="row w-100 mx-0">
          <div className="col-md-12 d-flex px-0">
            <div className="card flex-fill">
              <div className="card-body">
                <CardHeading width={170} withAction={false} />
                <div className="border rounded">
                  <div className="row g-0">
                    {[0, 1, 2].map((index) => (
                      <div className="col-md-4" key={index}>
                        <div className={`p-3 d-flex flex-column align-items-center${index < 2 ? " border-end" : ""}`}>
                          <SkeletonBlock width={40} height={40} className="mb-2" />
                          <SkeletonBlock width="50%" height={12} className="mb-2" />
                          <SkeletonBlock width="40%" height={18} className="mb-3" />
                          <div className="d-flex align-items-center gap-2">
                            <SkeletonBlock width={44} height={18} shape="pill" />
                            <SkeletonBlock width={72} height={12} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-12 d-flex px-0">
            <div className="card flex-fill">
              <div className="card-body">
                <CardHeading width={120} />
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <SkeletonBlock width={110} height={12} className="mb-2" />
                    <SkeletonBlock width={80} height={18} className="mb-3" />
                    <SkeletonBlock width={70} height={12} className="mb-2" />
                    <SkeletonBlock width={80} height={18} />
                  </div>
                  <div className="d-flex align-items-end gap-2" style={{ height: 90 }}>
                    <SkeletonBlock width={22} height="100%" />
                    <SkeletonBlock width={22} height="70%" />
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <SkeletonBlock width={44} height={18} shape="pill" />
                  <SkeletonBlock width={100} height={12} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-xxl-5 d-flex">
        <div className="card flex-fill">
          <div className="card-body">
            <CardHeading width={240} />
            <HorizontalBars widths={[78, 62, 50, 38, 26]} />
            <SegmentGridSkeleton />
          </div>
        </div>
      </div>
    </div>

    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <CardHeading width={220} />
            <div className="d-flex align-items-center justify-content-between mb-3">
              <SkeletonBlock width={240} height={12} />
              <div className="d-flex gap-2">
                <SkeletonBlock width={110} height={22} />
                <SkeletonBlock width={100} height={22} />
              </div>
            </div>
            <div className="d-flex gap-3" style={{ height: 300 }}>
              <div className="d-flex flex-column justify-content-between">
                {Array.from({ length: 6 }, (_, index) => (
                  <SkeletonBlock key={index} width={40} height={10} />
                ))}
              </div>
              <SkeletonBlock className="flex-fill" height="100%" style={{ borderRadius: 8 }} />
            </div>
            <div className="d-flex align-items-center justify-content-center gap-4 mt-3">
              <SkeletonBlock width={200} height={14} />
              <SkeletonBlock width={150} height={14} />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="row">
      <div className="col-xl-6 d-flex">
        <div className="card flex-fill">
          <div className="card-body">
            <CardHeading width={150} />
            <HorizontalBars widths={[72, 54, 36]} />
            <SegmentGridSkeleton />
          </div>
        </div>
      </div>
      <div className="col-xl-6 d-flex">
        <div className="card flex-fill">
          <div className="card-body">
            <CardHeading width={120} />
            {[0, 1, 2].map((index) => (
              <div key={index} className="p-4 nd-skeleton-panel rounded mb-2 d-flex align-items-center gap-4">
                <div className="flex-fill">
                  <SkeletonBlock width="45%" height={12} className="mb-2" />
                  <SkeletonBlock width="30%" height={20} />
                </div>
                <div className="flex-fill">
                  <SkeletonBlock width="40%" height={12} className="mb-2" />
                  <SkeletonBlock width="30%" height={20} />
                </div>
                <SkeletonBlock width={52} height={26} shape="pill" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default RevenueSummarySkeleton;
